import { useMemo, useState } from 'react';

const formatUsd = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

export default function RoiCalculatorSection() {
  const [monthlyLeads, setMonthlyLeads] = useState(40);
  const [avgTicket, setAvgTicket] = useState(2200);
  const [closeRate, setCloseRate] = useState(22);

  const numbers = useMemo(() => {
    const currentClosed = monthlyLeads * (closeRate / 100);
    const currentRevenue = currentClosed * avgTicket;

    const assumedLiftPct = 18;
    const projectedClosed = currentClosed * (1 + assumedLiftPct / 100);
    const projectedRevenue = projectedClosed * avgTicket;
    const liftRevenue = projectedRevenue - currentRevenue;

    return {
      currentRevenue,
      projectedRevenue,
      liftRevenue,
      projectedCloseRate: Number((closeRate * (1 + assumedLiftPct / 100)).toFixed(1)),
      assumedLiftPct,
    };
  }, [monthlyLeads, avgTicket, closeRate]);

  return (
    <section className="bg-[#f8f5ef] px-6 py-20">
      <div className="container mx-auto max-w-6xl rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_24px_60px_-50px_rgba(15,23,42,0.45)] md:p-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Revenue impact estimator</p>
            <h2 className="mt-3 text-3xl font-extrabold text-slate-900 md:text-4xl">See what financing can add this month</h2>
            <p className="mt-4 text-slate-600">Dial in your numbers to estimate lift from faster quote-to-checkout conversion.</p>

            <div className="mt-8 space-y-6">
              <label className="block">
                <div className="mb-2 flex items-center justify-between text-sm text-slate-600">
                  <span>Monthly quote opportunities</span>
                  <span className="text-xl font-bold text-slate-900">{monthlyLeads}</span>
                </div>
                <input
                  type="range"
                  min={10}
                  max={200}
                  value={monthlyLeads}
                  onChange={(e) => setMonthlyLeads(Number(e.target.value))}
                  className="h-3 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-emerald-600"
                />
              </label>

              <label className="block">
                <div className="mb-2 flex items-center justify-between text-sm text-slate-600">
                  <span>Average ticket</span>
                  <span className="text-xl font-bold text-slate-900">{formatUsd(avgTicket)}</span>
                </div>
                <input
                  type="range"
                  min={500}
                  max={25000}
                  step={100}
                  value={avgTicket}
                  onChange={(e) => setAvgTicket(Number(e.target.value))}
                  className="h-3 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-emerald-600"
                />
              </label>

              <label className="block">
                <div className="mb-2 flex items-center justify-between text-sm text-slate-600">
                  <span>Current close rate</span>
                  <span className="text-xl font-bold text-slate-900">{closeRate}%</span>
                </div>
                <input
                  type="range"
                  min={5}
                  max={70}
                  value={closeRate}
                  onChange={(e) => setCloseRate(Number(e.target.value))}
                  className="h-3 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-emerald-600"
                />
              </label>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Projected with LendLifted</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs text-slate-500">Current monthly revenue</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{formatUsd(numbers.currentRevenue)}</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-xs text-emerald-700">Projected monthly revenue</p>
                <p className="mt-1 text-2xl font-bold text-emerald-900">{formatUsd(numbers.projectedRevenue)}</p>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-emerald-200 bg-slate-950 p-5 text-white">
              <p className="text-xs uppercase tracking-[0.12em] text-emerald-200">Projected lift</p>
              <p className="mt-2 text-5xl font-extrabold text-emerald-300 [text-shadow:0_0_20px_rgba(16,185,129,0.5)]">
                {formatUsd(numbers.liftRevenue)}
              </p>
              <p className="mt-2 text-sm text-slate-300">
                Assumes {numbers.assumedLiftPct}% conversion lift and {numbers.projectedCloseRate}% projected close rate.
              </p>
              <p className="mt-4 text-sm font-semibold text-emerald-200">Stop leaving {formatUsd(numbers.liftRevenue)} on the table.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
